// T-157: the platform's calendars. Every date is a Julian day number (JDN) — what rules.ts converts with — and a calendar only
// says how a day is named, written and laid out in a month. The database keeps Gregorian (or UTC) dates; the server reads
// both written forms (GridDates.TryParse) and tells them apart by the year (1300–1499 Jalali, 1900–2199 Gregorian).
import { d2g, d2j, g2d, j2d, jalaliMonthLength, latinDigits } from './rules.ts';

export type CalendarId = 'jalali' | 'gregorian';

export interface CalendarDate {
  y: number;
  m: number;
  d: number;
}

export interface Calendar {
  id: CalendarId;
  fromJdn(jdn: number): CalendarDate;
  toJdn(y: number, m: number, d: number): number;
  monthLength(y: number, m: number): number;
  /** Month names, 1-based month = index + 1. */
  months: readonly string[];
  /** Weekday names in the calendar's column order (first column first). */
  weekdaysShort: readonly string[];
  weekdaysLong: readonly string[];
  /** The column of a day in a week row: 0 … 6, the calendar's first weekday first. */
  weekCol(jdn: number): number;
  /** Columns of the weekend (shown muted). */
  weekend: readonly number[];
  /** How a picked day is written into a field (and sent to the server). */
  format(jdn: number): string;
  /** The day a typed text names, or null (an impossible day included). */
  parse(text: string): number | null;
  /** «Sunday 5 October 2025», for labels. */
  longDate(jdn: number): string;
}

const mod = (a: number, b: number): number => ((a % b) + b) % b;
const pad2 = (n: number): string => String(n).padStart(2, '0');

function parts(text: string): [number, number, number] | null {
  const m = latinDigits(text || '')
    .trim()
    .match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  return m ? [+m[1], +m[2], +m[3]] : null;
}

const JALALI_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];
const JALALI_WD = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const JALALI_WDL = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];

export const jalali: Calendar = {
  id: 'jalali',
  fromJdn: jdn => {
    const j = d2j(jdn);
    return { y: j.jy, m: j.jm, d: j.jd };
  },
  toJdn: j2d,
  monthLength: jalaliMonthLength,
  months: JALALI_MONTHS,
  weekdaysShort: JALALI_WD,
  weekdaysLong: JALALI_WDL,
  weekCol: jdn => mod(jdn + 2, 7), // 0 = Saturday … 6 = Friday
  weekend: [6],
  format: jdn => {
    const j = d2j(jdn);
    return j.jy + '/' + pad2(j.jm) + '/' + pad2(j.jd);
  },
  parse: text => {
    const p = parts(text);
    if (!p) return null;
    const [y, m, d] = p;
    return y >= 1300 && y <= 1499 && m >= 1 && m <= 12 && d >= 1 && d <= jalaliMonthLength(y, m) ? j2d(y, m, d) : null;
  },
  longDate: jdn => {
    const j = d2j(jdn);
    return JALALI_WDL[mod(jdn + 2, 7)] + ' ' + j.jd + ' ' + JALALI_MONTHS[j.jm - 1] + ' ' + j.jy;
  },
};

const gregorianLength = (y: number, m: number): number =>
  m === 2 ? (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 29 : 28) : [4, 6, 9, 11].includes(m) ? 30 : 31;

/** Gregorian month and weekday names in the page's language (Persian or English); Monday first, ISO weeks. */
function gregorianNames(lang: string): { months: string[]; wd: string[]; wdl: string[] } {
  if (lang.startsWith('fa'))
    return {
      months: [
        'ژانویه',
        'فوریه',
        'مارس',
        'آوریل',
        'مه',
        'ژوئن',
        'ژوئیه',
        'اوت',
        'سپتامبر',
        'اکتبر',
        'نوامبر',
        'دسامبر',
      ],
      wd: ['د', 'س', 'چ', 'پ', 'ج', 'ش', 'ی'],
      wdl: ['دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه', 'یکشنبه'],
    };
  return {
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    wd: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    wdl: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  };
}

export function gregorian(lang = 'fa'): Calendar {
  const n = gregorianNames(lang);
  return {
    id: 'gregorian',
    fromJdn: jdn => {
      const g = d2g(jdn);
      return { y: g.gy, m: g.gm, d: g.gd };
    },
    toJdn: g2d,
    monthLength: gregorianLength,
    months: n.months,
    weekdaysShort: n.wd,
    weekdaysLong: n.wdl,
    weekCol: jdn => mod(jdn, 7), // a JDN mod 7 of 0 is a Monday
    weekend: [5, 6],
    format: jdn => {
      const g = d2g(jdn);
      return g.gy + '-' + pad2(g.gm) + '-' + pad2(g.gd);
    },
    parse: text => {
      const p = parts(text);
      if (!p) return null;
      const [y, m, d] = p;
      return y >= 1900 && y <= 2199 && m >= 1 && m <= 12 && d >= 1 && d <= gregorianLength(y, m) ? g2d(y, m, d) : null;
    },
    longDate: jdn => {
      const g = d2g(jdn);
      return n.wdl[mod(jdn, 7)] + ' ' + g.gd + ' ' + n.months[g.gm - 1] + ' ' + g.gy;
    },
  };
}

/** The calendar a field asks for (data-ds-calendar), else the page's (html data-ds-calendar), else Jalali. */
export function calendarFor(id: string | undefined, lang = 'fa'): Calendar {
  return id === 'gregorian' ? gregorian(lang) : jalali;
}
